import { useEffect, useId, useMemo, useState } from 'react'

import { OrderCodeLink } from './order-code-link'
import { ProductListCell } from './product-list-cell'
import type {
    CreatePaymentReceiptRequest,
    DuplicateReceiptInfo,
    ReceiptPaymentMethod,
} from '@/lib/api/customer-debt-api'
import { ApiError } from '@/lib/api/client'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MoneyPreview } from '@/components/ui/money-preview'
import { Textarea } from '@/components/ui/textarea'
import { useCustomerDebts } from '@/hooks/use-customer-query'
import { useCreatePaymentReceipt } from '@/hooks/use-customer-debt-query'
import {
    computeFifoAllocation,
    toOutstandingOrders,
} from '@/lib/debt-allocation'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { todayYMD } from '@/lib/date-presets'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'

interface Props {
    customerId: string
    // Số dư nợ hiện tại của khách (current_debt). Dương = khách đang nợ. Dùng để
    // tách phần "trừ vào nợ" vs "thành credit trả trước" — KHÔNG dựa vào đơn
    // per-order (nợ có thể ở mức ledger do điều chỉnh/đầu kỳ, không gắn đơn).
    currentDebt: number
    open: boolean
    onOpenChange: (open: boolean) => void
}

const METHODS: { value: ReceiptPaymentMethod; getLabel: () => string }[] = [
    { value: 'cash', getLabel: () => m.payment_method_cash() },
    { value: 'bank_transfer', getLabel: () => m.payment_method_bank() },
    { value: 'card', getLabel: () => m.customer_debt_payment_method_card() },
]

// Ô nhập số tiền phân bổ 1 đơn (dùng chung desktop/mobile).
function AllocInput({
    value,
    max,
    onChange,
    className,
}: {
    value?: number
    max: number
    onChange: (v: number) => void
    className?: string
}) {
    // Mỗi ô (bảng desktop + card mobile) cần id preview riêng.
    const previewId = useId()
    return (
        <div className={className ?? 'w-32'}>
            <Input
                type="number"
                min={0}
                step={1}
                max={max}
                value={value || ''}
                onChange={(e) => onChange(Number(e.target.value))}
                aria-describedby={previewId}
            />
            <MoneyPreview id={previewId} value={value || ''} />
        </div>
    )
}

export function CustomerDebtPaymentDialog({
    customerId,
    currentDebt,
    open,
    onOpenChange,
}: Props) {
    const workflow = useWorkflowTracking('customer_debt_payment', {
        active: open,
        attemptKey: customerId,
        operation: 'create',
    })
    const [amount, setAmount] = useState(0)
    const [method, setMethod] = useState<ReceiptPaymentMethod>('cash')
    const [occurredAt, setOccurredAt] = useState(todayYMD())
    const [note, setNote] = useState('')
    // Số tiền phân bổ vào từng đơn (theo sales_order_id). Luôn sửa được trực tiếp.
    const [alloc, setAlloc] = useState<Record<string, number>>({})
    // Server 409 duplicate_receipt → giữ payload + phiếu cũ, hiện confirm
    // "vẫn tạo?" (null = confirm đóng). Form giữ nguyên giá trị khi hủy.
    const [dupConfirm, setDupConfirm] = useState<{
        payload: CreatePaymentReceiptRequest
        existing?: DuplicateReceiptInfo
    } | null>(null)

    const { data: debtsData } = useCustomerDebts(customerId)
    const createReceipt = useCreatePaymentReceipt(customerId)

    const orders = useMemo(
        () => toOutstandingOrders(debtsData?.data ?? []),
        [debtsData],
    )

    // Reset toàn bộ mỗi lần mở lại để không giữ state phiếu thu cũ.
    useEffect(() => {
        if (open) {
            setAmount(0)
            setMethod('cash')
            setOccurredAt(todayYMD())
            setNote('')
            setAlloc({})
            setDupConfirm(null)
        }
    }, [open])

    // Nhập tổng số tiền → tự phân bổ FIFO (đơn cũ → mới) vào bảng.
    function handleAmountChange(value: number) {
        workflow.interact('payment')
        setAmount(value)
        const fifo = computeFifoAllocation(orders, value)
        const seed: Record<string, number> = {}
        for (const line of fifo.lines) {
            if (line.allocated > 0) seed[line.sales_order_id] = line.allocated
        }
        setAlloc(seed)
    }

    // Sửa số tiền phân bổ 1 đơn → cập nhật lại tổng số tiền bên trên.
    function handleAllocChange(orderId: string, value: number) {
        workflow.interact('payment')
        const next = { ...alloc, [orderId]: value }
        setAlloc(next)
        setAmount(Object.values(next).reduce((s, v) => s + (v || 0), 0))
    }

    // Tách theo SỐ DƯ NỢ (current_debt), không theo đơn per-order:
    // - toDebt: phần trừ vào nợ hiện tại
    // - credit: phần vượt nợ → thành credit trả trước
    const owed = Math.max(0, currentDebt)
    const toDebt = Math.min(amount, owed)
    const credit = Math.max(0, amount - owed)
    const remainingDebt = Math.max(0, owed - amount)

    // Gửi payload; nếu server báo phiếu trùng (409 duplicate_receipt) thì mở
    // confirm thay vì toast lỗi — người dùng bấm "Vẫn tạo" mới gửi lại force.
    async function submitPayload(payload: CreatePaymentReceiptRequest) {
        try {
            await workflow.run(
                () => createReceipt.mutateAsync(payload),
                (result) => result.data?.receipt.id,
            )
            setDupConfirm(null)
            onOpenChange(false)
        } catch (err) {
            if (err instanceof ApiError && err.code === 'duplicate_receipt') {
                const existing = (
                    err.details as
                        { existing?: DuplicateReceiptInfo } | undefined
                )?.existing
                workflow.step('warning_shown')
                setDupConfirm({ payload, existing })
                return
            }
            // Lỗi khác: hook-level onError đã toast, form giữ nguyên.
        }
    }

    async function handleSubmit() {
        const entries = Object.entries(alloc)
            .filter(([, v]) => v > 0)
            .map(([sales_order_id, v]) => ({ sales_order_id, amount: v }))
        await submitPayload({
            amount,
            payment_method: method,
            // Ngày = hôm nay → gửi undefined để BE đóng dấu giờ thật (now()),
            // tránh phiếu thu bị lưu 00:00 rồi hiện "07:00" lệch dòng thời gian
            // so với đơn bán. Chỉ gửi ngày khi người dùng backdate (chọn quá khứ).
            occurred_at:
                occurredAt && occurredAt !== todayYMD()
                    ? occurredAt
                    : undefined,
            note: note || undefined,
            allocations: entries.length > 0 ? entries : undefined,
        })
    }

    const canSubmit = amount > 0 && !createReceipt.isPending

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{m.customer_debt_payment_title()}</DialogTitle>
                </DialogHeader>

                <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                        <Label htmlFor="pay-amount" className="mb-1 block">
                            {m.customer_debt_payment_amount()}
                        </Label>
                        <Input
                            id="pay-amount"
                            type="number"
                            min={1}
                            step={1}
                            value={amount || ''}
                            onChange={(e) =>
                                handleAmountChange(Number(e.target.value))
                            }
                            aria-describedby="pay-amount-preview"
                        />
                        <MoneyPreview
                            id="pay-amount-preview"
                            value={amount || ''}
                        />
                    </div>
                    <div>
                        <Label htmlFor="pay-method" className="mb-1 block">
                            {m.customer_debt_payment_method()}
                        </Label>
                        <Select
                            value={method}
                            onValueChange={(v) => {
                                workflow.step('selection_changed')
                                setMethod(v as ReceiptPaymentMethod)
                            }}
                        >
                            <SelectTrigger id="pay-method">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {METHODS.map((mth) => (
                                    <SelectItem
                                        key={mth.value}
                                        value={mth.value}
                                    >
                                        {mth.getLabel()}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="pay-date" className="mb-1 block">
                            {m.customer_debt_payment_date()}
                        </Label>
                        <Input
                            id="pay-date"
                            type="date"
                            value={occurredAt}
                            onChange={(e) => {
                                workflow.interact('details')
                                setOccurredAt(e.target.value)
                            }}
                        />
                    </div>
                    <div>
                        <Label htmlFor="pay-note" className="mb-1 block">
                            {m.customer_debt_payment_note()}
                        </Label>
                        <Textarea
                            id="pay-note"
                            value={note}
                            onChange={(e) => {
                                workflow.interact('details')
                                setNote(e.target.value)
                            }}
                        />
                    </div>
                </div>

                {/* Phân bổ vào đơn còn nợ — cột "Phân bổ" sửa trực tiếp được.
                    Responsive: desktop = bảng, mobile = danh sách card. */}
                {orders.length > 0 && (
                    <div className="space-y-2">
                        <span className="text-sm font-medium">
                            {m.customer_debt_payment_allocation_preview()}
                        </span>

                        {/* Desktop */}
                        <div className="hidden md:block">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>
                                            {m.customer_debt_payment_order_col()}
                                        </TableHead>
                                        <TableHead>
                                            {m.customer_debt_payment_products_col()}
                                        </TableHead>
                                        <TableHead>
                                            {m.customer_debt_payment_remaining_col()}
                                        </TableHead>
                                        <TableHead>
                                            {m.customer_debt_payment_alloc_col()}
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {orders.map((o) => (
                                        <TableRow key={o.sales_order_id}>
                                            <TableCell className="whitespace-nowrap font-mono text-xs">
                                                <OrderCodeLink
                                                    orderId={o.sales_order_id}
                                                    code={o.order_code}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <ProductListCell
                                                    items={o.items}
                                                />
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                {formatVnd(o.remaining_amount)}
                                            </TableCell>
                                            <TableCell>
                                                <AllocInput
                                                    value={
                                                        alloc[o.sales_order_id]
                                                    }
                                                    max={o.remaining_amount}
                                                    onChange={(v) =>
                                                        handleAllocChange(
                                                            o.sales_order_id,
                                                            v,
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>

                        {/* Mobile */}
                        <div className="space-y-2 md:hidden">
                            {orders.map((o) => (
                                <div
                                    key={o.sales_order_id}
                                    className="space-y-2 rounded-md border p-3"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="font-mono text-xs">
                                            <OrderCodeLink
                                                orderId={o.sales_order_id}
                                                code={o.order_code}
                                            />
                                        </span>
                                        <span className="text-sm font-medium">
                                            {formatVnd(o.remaining_amount)}
                                        </span>
                                    </div>
                                    <ProductListCell items={o.items} />
                                    {/* items-start + leading-11 (= chiều cao ô
                                        mobile) giữ nhãn thẳng hàng với ô nhập,
                                        không bị preview bên dưới kéo lệch. */}
                                    <div className="flex items-start justify-between gap-2">
                                        <span className="text-xs leading-11 text-muted-foreground">
                                            {m.customer_debt_payment_alloc_col()}
                                        </span>
                                        <AllocInput
                                            value={alloc[o.sales_order_id]}
                                            max={o.remaining_amount}
                                            onChange={(v) =>
                                                handleAllocChange(
                                                    o.sales_order_id,
                                                    v,
                                                )
                                            }
                                            className="w-40"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {amount > 0 && (toDebt > 0 || credit > 0) && (
                    <div
                        data-testid="payment-preview"
                        className="space-y-1 rounded-md border bg-muted/30 p-2.5 text-sm"
                    >
                        {toDebt > 0 && (
                            <div>
                                <span className="font-semibold text-red-600">
                                    {formatVnd(toDebt)}
                                </span>{' '}
                                {m.customer_debt_payment_applied_to_debt()}
                                {remainingDebt > 0 && (
                                    <>
                                        {' ('}
                                        {m.customer_debt_payment_still_owed()}{' '}
                                        {formatVnd(remainingDebt)}
                                        {')'}
                                    </>
                                )}
                            </div>
                        )}
                        {credit > 0 && (
                            <div>
                                <span className="font-semibold text-green-600">
                                    {formatVnd(credit)}
                                </span>{' '}
                                {m.customer_debt_payment_leftover_credit()}
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={createReceipt.isPending}
                    >
                        {m.common_cancel()}
                    </Button>
                    <Button onClick={handleSubmit} disabled={!canSubmit}>
                        {createReceipt.isPending
                            ? m.common_processing()
                            : m.customer_debt_payment_submit()}
                    </Button>
                </div>

                {/* Confirm phiếu thu trùng: server 409 duplicate_receipt.
                    "Vẫn tạo" gửi lại với force=true; "Hủy" giữ nguyên form. */}
                <Dialog
                    open={Boolean(dupConfirm)}
                    onOpenChange={(o) => !o && setDupConfirm(null)}
                >
                    <DialogContent className="max-w-sm">
                        <DialogHeader>
                            <DialogTitle>
                                {m.customer_debt_dup_receipt_title()}
                            </DialogTitle>
                            <DialogDescription>
                                {m.customer_debt_dup_receipt_desc()}
                            </DialogDescription>
                        </DialogHeader>
                        {dupConfirm?.existing && (
                            <div className="space-y-1 rounded-md border bg-muted/30 p-2.5 text-sm">
                                <div className="font-medium">
                                    {formatVnd(dupConfirm.existing.amount)}
                                </div>
                                {dupConfirm.existing.note && (
                                    <div className="text-muted-foreground">
                                        {dupConfirm.existing.note}
                                    </div>
                                )}
                            </div>
                        )}
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setDupConfirm(null)}
                            >
                                {m.customer_debt_dup_receipt_cancel()}
                            </Button>
                            <Button
                                disabled={createReceipt.isPending}
                                onClick={() => {
                                    if (!dupConfirm) return
                                    workflow.step('warning_confirmed')
                                    void submitPayload({
                                        ...dupConfirm.payload,
                                        force: true,
                                    })
                                }}
                            >
                                {createReceipt.isPending
                                    ? m.common_processing()
                                    : m.customer_debt_dup_receipt_confirm()}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </DialogContent>
        </Dialog>
    )
}
